<template>
  <v-dialog v-model="dialog" fullscreen persistent hide-overlay transition="dialog-bottom-transition">
    <template v-slot:activator="{ on }">
      <ButtonDefault
        :button-name="$t('shooting.buttons.reprocess')"
        :disabled="shooting.length === 0"
        :outlined="true"
        @click="getStamps"
        v-on="on"
      />
    </template>
    <v-card class="custom-card" tile>
      <v-toolbar dark color="primary">
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div v-if="!loading" class="reprocess-block">
        <div class="image-block">
          <div class="inner-img" :style="{ 'background-image': imagesToDisplay }">
            <img v-if="selectedStamp" class="stamp" :src="selectedStamp.url" alt="" />
          </div>
          <v-btn @click="prevImages" class="image-block_btn image-block_btn__left" small fab
            ><v-icon>keyboard_arrow_left</v-icon></v-btn
          >
          <v-btn
            @click="nextImages"
            :disabled="shootingImageWithoutImageLabel(images).length === index - 1"
            class="image-block_btn image-block_btn__right"
            small
            fab
            ><v-icon>keyboard_arrow_right</v-icon></v-btn
          >
        </div>

        <div class="settings-block">
          <div class="settings-block-inner">
            <div class="center-flex">
              <p class="title-stamp">{{ $t('reprocess.label') }}</p>
            </div>
            <v-select
              v-model="selectedStamp"
              return-object
              item-value="id"
              item-text="name"
              :items="stampsWithFormatRestriction"
              :label="maskNotAppliedLabel"
              :placeholder="$t('reprocess.reprocessing-stamp-dropdown-placeholder')"
              persistent-placeholder
            ></v-select>
            <v-alert v-if="isIncompatibleStamp" type="error" text dense>
              {{ $t('reprocess.incompatibleStamp') }}
            </v-alert>
            <v-btn
              :loading="loadingSend"
              :disabled="!selectedStamp || isIncompatibleStamp"
              color="primary"
              outlined
              @click="sendReprocess"
              >{{ $t('reprocess.valid') }}</v-btn
            >
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import ButtonDefault from '../common/atoms/ButtonDefault.vue'
export default {
  components: {
    ButtonDefault
  },
  name: 'ReprocessingButton',
  props: {
    images: {
      type: Array,
      default: () => []
    },
    shootingId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      loadingSend: false,
      selectedStamp: null,
      index: 0
    }
  },
  computed: {
    ...mapGetters(['userId']),
    ...mapState({
      shooting: (state) => state.shooting,
      stamps: (state) => state.stamps
    }),
    currentStampId() {
      const firstImage = this.shooting[0]
      return firstImage && firstImage.stampId
    },
    stampsWithFormatRestriction() {
      const shootingImage = this.shooting[0]
      if (!shootingImage) return []
      return this.stamps.filter((stamp) => stamp.format === shootingImage.format)
    },
    maskNotAppliedLabel() {
      if (this.selectedStamp) return ''
      return !this.currentStampId ? this.$t('reprocess.maskNotApplied') : ''
    },
    imagesToDisplay() {
      const image = this.images[this.index]
      if (!image) return 'none'
      return `url('${image.url}')`
    },
    isIncompatibleStamp() {
      const config = this.selectedStamp && this.selectedStamp.croppingConfiguration
      if (!config) return false
      const isWallAndFloor = config.croppingType === 'BACK_FLOOR_CROPPING'
      const hasNextlaneAI =
        config.exteriorCroppingProvider === 'NEXTLANE_AI' || config.interiorCroppingProvider === 'NEXTLANE_AI'
      return isWallAndFloor && hasNextlaneAI
    }
  },
  watch: {
    selectedStamp(newStamp) {
      if (newStamp && this.isIncompatibleStamp) {
        const { exteriorCroppingProvider, interiorCroppingProvider, croppingType } = newStamp.croppingConfiguration
        console.error(
          `Reprocessing not allowed — exterior provider: ${exteriorCroppingProvider}, interior provider: ${interiorCroppingProvider}, croppingType: ${croppingType}`
        )
      }
    }
  },
  created() {
    this.eventKey = this.eventKeyCode.bind(this)
  },
  methods: {
    shootingImageWithoutImageLabel(shooting) {
      return shooting.filter((s) => !s.isLabelImage)
    },
    prevImages() {
      if (this.index > 0) this.index = this.index - 1
    },
    nextImages() {
      const shooting = this.shootingImageWithoutImageLabel(this.images)

      if (this.index < shooting.length - 1) this.index = this.index + 1
    },
    preselectCurrentStamp() {
      if (this.currentStampId && this.stampsWithFormatRestriction.length > 0) {
        const match = this.stampsWithFormatRestriction.find((s) => s.id === this.currentStampId)
        if (match) {
          this.selectedStamp = match
        }
      }
    },
    async getStamps() {
      this.loading = true
      const res = await this.$store.dispatch('getStamps', { userId: this.userId })
      this.loading = false
      if (res) {
        this.preselectCurrentStamp()
        window.addEventListener('keydown', this.eventKey)
      }
    },
    eventKeyCode(ev) {
      if (ev.keyCode === 37 && this.index > 0) this.index = this.index - 1
      if (ev.keyCode === 39 && this.index < this.images.length - 1) this.index = this.index + 1
    },
    async sendReprocess() {
      if (this.selectedStamp && this.selectedStamp.id != null) {
        this.loadingSend = true
        const res = await this.$store.dispatch('reprocessShooting', {
          shootingId: this.shootingId,
          stampId: this.selectedStamp.id
        })
        this.loadingSend = false
        if (res) {
          this.$store.commit('SET_SNACKBAR', {
            snack: this.$t('reprocess.validateMessage'),
            color: 'success'
          })
          this.closeAll()
        }
      }
    },
    closeModal() {
      this.dialog = false
      this.selectedStamp = null
      this.index = 0
      window.removeEventListener('keydown', this.eventKey)
    },
    closeAll() {
      this.closeModal()
      this.$router.push('/shootings')
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.eventKey)
  }
}
</script>

<style lang="less" scoped>
.custom-card {
  height: 100%;
  overflow: hidden;
}

.reprocess-block {
  display: flex;
  height: calc(100% - 54px);
}

.image-block {
  position: relative;
  width: 60%;
  overflow: hidden;
}

.image-block_btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.image-block_btn__left {
  left: 20px;
}

.image-block_btn__right {
  right: 20px;
}

.inner-img {
  position: relative;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  transform: translate(-50%, -50%);
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% auto;
  filter: drop-shadow(0 1px 5px gray);
}

.title-stamp {
  color: rgba(0, 0, 0, 0.6);
}

.center-flex {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.settings-block {
  width: 40%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.1);
}

.settings-block-inner {
  min-height: 200px;
  padding: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
}

.stamp {
  position: relative;
  display: block;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: auto;
}
</style>
